import { Center, Spinner } from '@chakra-ui/react';
import { HTTP } from '@utils/http';
import { useUserContext } from 'providers/useUserContext';
import { useEffect, useState } from 'react';
import { MindbodyUser } from 'types/client';
import { Login } from './Login';
import { Register } from './Register';
import * as Sentry from '@sentry/react';
import { sentryTrace } from 'utils/sentryTrace';

function Root() {
  const { isLoggedIn, setUser } = useUserContext();
  const { oauth } = mondayMindbody ?? {};
  const { user } = oauth;
  const [isCheckingStatus, setIsCheckingStatus] = useState(!!(user && !isLoggedIn));
  // Track if we should show login instead of register (when session expired but user exists in Mindbody)
  const [sessionExpired, setSessionExpired] = useState(false);

  console.log('[OAuth Root] Component mounted/updated', {
    hasOAuthUser: !!user,
    isLoggedIn,
    oauthUserEmail: user?.email,
    oauthUserName: `${user?.given_name} ${user?.family_name}`,
    isCheckingStatus,
    sessionExpired,
  });

  Sentry.addBreadcrumb({
    category: 'oauth',
    message: 'OAuth Root component rendered',
    level: 'info',
    data: {
      hasOAuthUser: !!user,
      isLoggedIn,
      oauthUserEmail: user?.email,
      isCheckingStatus,
      sessionExpired,
    },
  });

  useEffect(() => {
    if (user && !isLoggedIn) {
      console.log('[OAuth Root] User exists in OAuth but not logged in - checking if user exists in Mindbody', {
        email: user.email,
      });

      Sentry.addBreadcrumb({
        category: 'oauth',
        message: 'Checking if user exists in Mindbody database',
        level: 'info',
        data: { email: user.email },
      });

      setIsCheckingStatus(true);
      HTTP.get<MindbodyUser>('/client/me')
        .send()
        .then((mindbodyUser) => {
          console.log('[OAuth Root] User found in Mindbody! Auto-logging in...', {
            userId: mindbodyUser.Id,
            email: mindbodyUser.Email,
            firstName: mindbodyUser.FirstName,
            lastName: mindbodyUser.LastName,
          });

          Sentry.addBreadcrumb({
            category: 'oauth',
            message: 'User found in Mindbody - auto login',
            level: 'info',
            data: {
              userId: mindbodyUser.Id,
              email: mindbodyUser.Email,
            },
          });

          sentryTrace('OAuth: User exists in Mindbody, auto-login successful', {
            level: 'info',
            tags: {
              flow: 'oauth',
              step: 'user_check_success',
            },
            extra: {
              userId: mindbodyUser.Id,
              email: mindbodyUser.Email,
            },
          });

          setUser(mindbodyUser);
        })
        .catch((error) => {
          // Check if the error is a 401 (session expired) vs 404 (user not found in Mindbody)
          // ApiClientException has Code property from the server response
          const errorCode = error?.Code || error?.status || error?.response?.status;
          const errorCodeStr = error?.ErrorCode || '';
          const isSessionExpiredError = errorCode === 401 || errorCodeStr === 'UserNotLoggedIn';

          console.log('[OAuth Root] /client/me request failed', {
            error: error?.Message || error?.message || error,
            errorCode,
            errorCodeStr,
            isSessionExpiredError,
            oauthEmail: user.email,
            willShow: isSessionExpiredError ? 'Login button (session expired)' : 'Register form (new user)',
          });

          if (isSessionExpiredError) {
            // Session expired - user needs to re-login, NOT register
            console.log('[OAuth Root] Session expired - showing login button instead of registration form');

            sentryTrace('OAuth: Session expired, showing login button', {
              level: 'info',
              tags: {
                flow: 'oauth',
                step: 'session_expired',
              },
              extra: {
                oauthEmail: user.email,
              },
            });

            setSessionExpired(true);
          } else {
            // User genuinely not found in Mindbody - show registration form
            console.log('[OAuth Root] User NOT found in Mindbody - showing registration form', {
              oauthEmail: user.email,
            });

            Sentry.addBreadcrumb({
              category: 'oauth',
              message: 'User not found in Mindbody - showing register form',
              level: 'info',
              data: {
                oauthEmail: user.email,
                error: error?.message || String(error),
              },
            });

            sentryTrace('OAuth: User not found in Mindbody, showing registration form', {
              level: 'info',
              tags: {
                flow: 'oauth',
                step: 'user_check_not_found',
              },
              extra: {
                oauthEmail: user.email,
                error: error?.message || String(error),
              },
            });
          }

          setIsCheckingStatus(false);
        });
    } else {
      console.log('[OAuth Root] No OAuth user or already logged in', {
        hasUser: !!user,
        isLoggedIn,
      });
      setIsCheckingStatus(false);
    }
  }, [user, isLoggedIn, setUser]);

  if (isCheckingStatus) {
    console.log('[OAuth Root] Showing spinner - checking user status...');
    return (
      <Center h="200px">
        <Spinner />
      </Center>
    );
  }

  // Show login button if:
  // 1. No OAuth user (normal case)
  // 2. Session expired (user exists but needs to re-authenticate)
  const shouldShowRegister = user && !isLoggedIn && !sessionExpired;
  console.log('[OAuth Root] Rendering decision', {
    shouldShowRegister,
    hasOAuthUser: !!user,
    isLoggedIn,
    sessionExpired,
    willShow: shouldShowRegister ? 'Register form' : 'Login button',
  });

  Sentry.addBreadcrumb({
    category: 'oauth',
    message: `Rendering ${shouldShowRegister ? 'Register form' : 'Login button'}`,
    level: 'info',
    data: {
      hasOAuthUser: !!user,
      isLoggedIn,
      sessionExpired,
    },
  });

  return shouldShowRegister ? <OAuth.Register /> : <OAuth.Login />;
}

export const OAuth = Object.assign(Root, {
  Login,
  Register,
});
